import http from '../http'
import type { 
  LoginRequest, 
  RegisterRequest, 
  TokenResponse, 
  User, 
  UserUpdateRequest,
  PasswordChangeRequest,
  SmsLoginRequest,
  SendSmsRequest
} from '@/types/auth'
import type { ApiResponse } from '@/types/api'

// 第三方登录请求类型
interface SocialLoginRequest {
  provider: string;
  provider_user_id: string;
  email: string;
  full_name?: string;
  avatar_url?: string;
}

// 短信发送响应类型
interface SendSmsResponse {
  success: boolean;
  message: string;
  countdown?: number;
}

// 临时测试函数，用于排查短信登录问题
export const testSmsLoginFlow = async (phone: string, smsCode: string) => {
  console.log('testSmsLoginFlow: 开始测试完整的短信登录流程');
  console.log('testSmsLoginFlow: 输入参数 - phone:', phone, 'smsCode:', smsCode);
  
  try {
    // 模拟LoginPage中的对象创建过程
    const smsLoginData = {
      phone: phone,
      sms_code: smsCode
    };
    
    console.log('testSmsLoginFlow: 创建的请求对象:', smsLoginData);
    console.log('testSmsLoginFlow: 对象包含sms_code字段:', 'sms_code' in smsLoginData);
    console.log('testSmsLoginFlow: 对象JSON序列化:', JSON.stringify(smsLoginData));
    
    // 调用实际的smsLogin函数
    const result = await authApi.smsLogin(smsLoginData);
    
    console.log('testSmsLoginFlow: 登录成功，结果:', result);
    return {
      success: true,
      data: result
    };
  } catch (error) {
    console.error('testSmsLoginFlow: 登录失败:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : String(error)
    };
  }
};

export const authApi = {
  // 用户登录
  login: (data: LoginRequest) => {
    console.log('authApi: login called with data', data);
    return http.post<ApiResponse<TokenResponse>>('/auth/login', data, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      transformRequest: [
        (data) => {
          const params = new URLSearchParams()
          params.append('username', data.username)
          params.append('password', data.password)
          return params
        },
      ],
    })
  },

  // 手机短信登录 - 简化实现，确保请求体正确发送
  smsLogin: async (data: SmsLoginRequest) => {
    console.log('authApi: smsLogin started with data', data);
    console.log('authApi: smsLogin data type:', typeof data);
    console.log('authApi: smsLogin has sms_code:', 'sms_code' in data);
    
    try {
      // 直接使用data作为请求体，避免额外的对象转换
      const config = {
        headers: {
          'Content-Type': 'application/json'
        }
      };
      
      // 打印完整的请求信息用于调试
      console.log('authApi: Preparing to send smsLogin request to /auth/phone-login');
      console.log('authApi: smsLogin full request details:', {
        data: JSON.stringify(data),
        config: JSON.stringify(config)
      });
      
      // 正确的路径应该包含/auth前缀，因为后端路由注册时添加了这个前缀
      console.log('authApi: Calling http.post for smsLogin');
      const response = await http.post<ApiResponse<TokenResponse>>('/auth/phone-login', data, config);
      
      console.log('authApi: smsLogin request completed successfully');
      console.log('authApi: smsLogin response status:', response.status);
      console.log('authApi: smsLogin response data:', response.data);
      
      return response;
    } catch (error) {
      console.error('authApi: smsLogin error:', error);
      console.error('authApi: Error details:', {
        message: error instanceof Error ? error.message : String(error),
        stack: error instanceof Error ? error.stack : undefined,
        response: (error as any)?.response?.data
      });
      throw error;
    }
  },

  // 发送短信验证码
  sendSmsCode: async (data: SendSmsRequest) => {
    console.log('authApi: sendSmsCode called with data', data);
    
    // 创建请求配置对象
    const config = {
      headers: {
        'Content-Type': 'application/json'
      }
    };
    
    // 打印完整的请求信息用于调试
    console.log('authApi: Preparing to send sendSmsCode request to /auth/sms/send');
    console.log('authApi: sendSmsCode full request details:', {
      data: JSON.stringify(data),
      config: JSON.stringify(config)
    });
    
    // 正确的路径应该包含/auth前缀，因为后端路由注册时添加了这个前缀
    console.log('authApi: Calling http.post for sendSmsCode');
    try {
      const response = await http.post<ApiResponse<SendSmsResponse>>('/auth/sms/send', data, config);
      
      console.log('authApi: sendSmsCode request completed successfully');
      console.log('authApi: sendSmsCode response status:', response.status);
      console.log('authApi: sendSmsCode response data:', response.data);
      
      return response;
    } catch (error) {
      console.error('authApi: sendSmsCode error:', error);
      console.error('authApi: Error details:', {
        message: error instanceof Error ? error.message : String(error),
        stack: error instanceof Error ? error.stack : undefined,
        response: (error as any)?.response?.data
      });
      throw error;
    }
  },

  // 第三方登录
  socialLogin: (data: SocialLoginRequest) => {
    console.log('authApi: socialLogin called with data', data);
    return http.post<ApiResponse<TokenResponse>>('/auth/social-login', data)
  },

  // 用户注册
  register: (data: RegisterRequest) => {
    console.log('authApi: register called with data', data);
    // 准备注册数据，移除confirm_password字段
    const { confirm_password, ...registerData } = data;
    return http.post<ApiResponse<User>>('/auth/register', registerData)
  },

  // 刷新令牌
  refreshToken: (refreshToken: string) => {
    console.log('authApi: refreshToken called with token', refreshToken);
    // 使用查询参数发送刷新令牌
    return http.post<ApiResponse<TokenResponse>>(`/auth/refresh?refresh_token=${encodeURIComponent(refreshToken)}`)
  },

  // 用户登出
  logout: () => {
    console.log('authApi: logout called');
    return http.post<ApiResponse>('/auth/logout')
  },

  // 忘记密码
  forgotPassword: (email: string) => {
    console.log('authApi: forgotPassword called with email', email);
    return http.post<ApiResponse>('/auth/forgot-password', { email })
  },

  // 重置密码
  resetPassword: (token: string, newPassword: string) => {
    console.log('authApi: resetPassword called with token', token);
    return http.post<ApiResponse>('/auth/reset-password', {
      token,
      new_password: newPassword,
    })
  },

  // 获取当前用户信息（使用新的profile端点）
  getCurrentUser: () => {
    console.log('authApi: getCurrentUser called');
    return http.get<ApiResponse<User>>('/profile/me')
  },

  // 更新当前用户信息（使用新的profile端点）
  updateCurrentUser: (data: UserUpdateRequest) => {
    console.log('authApi: updateCurrentUser called with data', data);
    return http.put<ApiResponse<User>>('/profile/me', data)
  },

  // 修改密码（使用新的profile端点）
  changePassword: (data: PasswordChangeRequest) => {
    console.log('authApi: changePassword called with data', data);
    return http.post<ApiResponse>('/profile/change-password', data)
  },

  // 删除账户
  deleteAccount: () => {
    console.log('authApi: deleteAccount called');
    return http.delete<ApiResponse>('/profile/me')
  },
}